/**
 * Sawyer 08 meds-feeding — pure display helpers shared by the report, the
 * printout and the med pass. No React, no I/O. Principle 5: every line says
 * where a fact came from when it was not set by a person ("house default",
 * "from frequency", "this stay").
 */
import type { CareDayDog, CareDayResponse, DoseStatusView, MealSource } from '../../lib/meds-feeding-api.js';
import { doseOutcomeLabel, formatClock, mealLabel } from '../../lib/care-labels.js';
import { humanizeFeedingMethod, humanizeFoodType } from '../dog-profile/format.js';

/** "Saturday, October 24" — noon-anchored so no timezone can move the day. */
export function longDateLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** The visit tag on a dog's line. */
export function visitTag(dog: Pick<CareDayDog, 'visitStatus' | 'arrivesToday' | 'leavesToday' | 'checkedOutAt'>, timeZone?: string): string {
  if (dog.visitStatus === 'checked_out') {
    const at = dog.checkedOutAt ? formatClock(dog.checkedOutAt, timeZone) : '';
    return at ? `Went home ${at}` : 'Went home';
  }
  if (dog.visitStatus === 'expected') return dog.arrivesToday ? 'Arrives today' : 'Expected';
  if (dog.leavesToday) return 'Going home';
  return 'Checked in';
}

export const MEAL_SOURCE_NOTE: Readonly<Record<MealSource, string | null>> = {
  set: null,
  house_default: '(house default)',
  meal_add_on: '(House Food add-on)',
};

/** "Breakfast — 1 cup kibble · Dry · Measured portions (house default)". */
export function mealLine(dog: CareDayDog, meal: { meal: string; source: MealSource }): string {
  const what = [dog.feeding.notes, humanizeFoodType(dog.feeding.foodType), humanizeFeedingMethod(dog.feeding.method)]
    .filter((p): p is string => Boolean(p && p.trim()))
    .join(' · ');
  const note = MEAL_SOURCE_NOTE[meal.source];
  const body = what.length > 0 ? `${mealLabel(meal.meal)} — ${what}` : `${mealLabel(meal.meal)} — no feeding instructions on file`;
  return note ? `${body} ${note}` : body;
}

/** "Administered · Dana · 8:04 AM" / "Prepared by Dana · 7:02 AM". */
export function doseStatusText(status: DoseStatusView, timeZone?: string): string | null {
  const l = status.latest;
  if (!l) return null;
  const at = formatClock(l.occurredAt, timeZone);
  const who = l.staffName ?? 'Staff';
  if (l.outcome === 'prepared') return `Prepared by ${who} · ${at}`;
  return `${doseOutcomeLabel(l.outcome)} · ${who} · ${at}`;
}

/** "Given 2× today · last 3:10 PM by Dana" for a PRN dose. */
export function prnText(status: DoseStatusView, timeZone?: string): string | null {
  const l = status.latest;
  if (!l || status.countToday === 0) return null;
  const n = status.countToday;
  return `Given ${n}× today · last ${formatClock(l.occurredAt, timeZone)} by ${l.staffName ?? 'Staff'}`;
}

/** Look up a dog / medication in the payload. */
export function indexCareDay(day: CareDayResponse) {
  const dogs = new Map(day.dogs.map((d) => [d.dogId, d]));
  const meds = new Map<string, CareDayDog['medications'][number]>();
  for (const d of day.dogs) for (const m of d.medications) meds.set(m.id, m);
  return { dogs, meds };
}

/**
 * Review round 2: the daycare dogs the Noon list does not show. The desk's
 * per-visit lunch control (Lunch today / No lunch / Usual) sits on a dog's
 * Noon entry, and a daycare dog only has one when it eats lunch or has a
 * Noon dose. Every other daycare dog in the day lands here, so each daycare
 * dog has exactly one lunch control: the dog with no lunch default can be
 * given "Lunch today", and a dog the desk set to "No lunch" can go back to
 * "Usual".
 */
export function daycareDogsOffNoonList(day: Pick<CareDayResponse, 'dogs' | 'groups'>): CareDayDog[] {
  const onNoon = new Set(day.groups.filter((g) => g.slot === 'midday').flatMap((g) => g.entries.map((e) => e.dogId)));
  return day.dogs.filter((d) => d.service === 'daycare' && !onNoon.has(d.dogId));
}

export function serviceLabel(service: 'boarding' | 'daycare'): string {
  return service === 'boarding' ? 'Boarding' : 'Daycare';
}
